import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Switch, Route, Redirect, useLocation, useRouteMatch } from 'react-router-dom'
import { Container, Hidden } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import clsx from 'clsx'

import { AnalyticsService, BOOKING_STEP_INDEX } from '../../services/analytics'

import { BookingStepper } from './components/stepper'
import { ServicesSummary } from './components/services-summary'
import { StepBook } from './components/step-book'
import { StepFinish } from './components/step-finish'
import { MobileBookingBar } from './components/mobile-booking-bar'
import { BookingBarProvider } from './components/booking-bar-context'
import { ANALYTICS_STEPS, STEPS, stepNumber } from './components/steps'
import { getStripe } from '../../lib/stripe'
import { firstBookingStepPath } from '../../lib/booking-flow'
import { trackDirectBookingEntry } from '../../lib/booking-entry'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

const DesktopSidebar = () => (
  <Hidden smDown>
    <ServicesSummary sidebar />
  </Hidden>
)

export const BookingPage = React.memo(() => {
  const classes = useStyles()
  const location = useLocation()
  const step = useRouteMatch('/booking/:step')?.params?.step

  useEffect(() => {
    if (step === STEPS.BOOK) AnalyticsService.trackImpressions(ANALYTICS_STEPS[step])
    // GA4 funnel signal. One event per step landed on; powers the
    // booking_step_viewed → ..._completed → purchase funnel report.
    if (step && BOOKING_STEP_INDEX[step] != null) {
      AnalyticsService.trackBookingStepViewed(step, BOOKING_STEP_INDEX[step])
    }
  }, [step])

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Stripe.js is not on the marketing pages any more; start fetching it as
  // soon as a booking begins so it is ready by the pay step.
  useEffect(() => { getStripe() }, [])

  // A cold load of a booking URL (ad, rebook link, bookmark) is an entry too:
  // booking_entry with entry_source 'direct_url', once (lib/booking-entry.js).
  useEffect(() => { trackDirectBookingEntry() }, [])

  // Direction-aware slide transitions between steps. Compare the current
  // step's numeric position against the previous one so going Next slides
  // the new screen in from the right and Previous slides it in from the left.
  const stepPos = stepNumber[step] ?? -1
  const prevStepPos = useRef(stepPos)
  const [direction, setDirection] = useState('fwd')
  useEffect(() => {
    if (stepPos !== prevStepPos.current) {
      setDirection(stepPos >= prevStepPos.current ? 'fwd' : 'back')
      prevStepPos.current = stepPos
    }
  }, [stepPos])
  const transitionClass = direction === 'fwd' ? 'slide-fwd' : 'slide-back'

  // Book & pay is the one booking screen; Finish stands alone.
  const isBook = step === STEPS.BOOK

  return (
    <BookingBarProvider>
      <div className={clsx('book-steps', classes.bookingShell)}>
        {isBook && <BookingStepper logo={true} />}

        <Container className={classes.container}>
          <TransitionGroup className="booking-transition-host">
            <CSSTransition key={location.key} classNames={transitionClass} timeout={300}>
              <Switch location={location}>
                <Route exact path="/booking/book" render={() => <StepBook sidebar={<DesktopSidebar />} />} />
                <Route exact path="/booking/finish" component={StepFinish} />
                {/* Bare /booking, any unknown step and the retired four-step screens
                    (details, address, service, extra, checkout; Rails 301s those) open
                    Book & pay, query kept. DECISIONS 2026-10-12. */}
                <Route render={({ location }) => <Redirect to={firstBookingStepPath(location.search)} />} />
              </Switch>
            </CSSTransition>
          </TransitionGroup>
        </Container>

        <MobileBookingBar />
      </div>
    </BookingBarProvider>
  )
})
